import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProgressGraphs.css';

const ProgressGraphs = () => {
    const navigate = useNavigate();
    const [muscleGroupProgress, setMuscleGroupProgress] = useState({});
    const [workoutHistory, setWorkoutHistory] = useState([]);
    const barChartRef = useRef(null);
    const pieChartRef = useRef(null);
    const lineChartRef = useRef(null);
    const [chartsLoaded, setChartsLoaded] = useState(false);

    useEffect(() => {
        loadProgressData();
        loadChartJS();
    }, []);

    const loadChartJS = async () => {
        // Load Chart.js from CDN
        if (!window.Chart) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/3.9.1/chart.min.js';
            script.onload = () => {
                setChartsLoaded(true);
            };
            document.head.appendChild(script);
        } else {
            setChartsLoaded(true);
        }
    };

    useEffect(() => {
        if (chartsLoaded && Object.keys(muscleGroupProgress).length > 0) {
            createCharts();
        }
    }, [chartsLoaded, muscleGroupProgress, workoutHistory]);

    const loadProgressData = () => {
        const workouts = JSON.parse(localStorage.getItem('workoutHistory') || '[]');
        setWorkoutHistory(workouts);
        calculateMuscleGroupProgress(workouts);
    };

    const calculateMuscleGroupProgress = (workouts) => {
        const progress = {};

        workouts.forEach(workout => {
            if (workout.exercises && workout.exercises.length > 0) {
                workout.exercises.forEach(exercise => {
                    const muscleGroup = getMuscleGroupFromExercise(exercise);
                    const reps = workout.repCount || 10;
                    progress[muscleGroup] = (progress[muscleGroup] || 0) + reps;
                });
            }
        });

        setMuscleGroupProgress(progress);
    };

    const getMuscleGroupFromExercise = (exercise) => {
        // Handle both string exercises and object exercises
        const exerciseName = typeof exercise === 'string' ? exercise : exercise.name;

        // Safety check
        if (!exerciseName || typeof exerciseName !== 'string') {
            return 'Other';
        }

        // If it's an object with muscleGroup property, use that directly
        if (typeof exercise === 'object' && exercise.muscleGroup) {
            return exercise.muscleGroup;
        }

        const exerciseNameLowerCase = exerciseName.toLowerCase();

        // ... rest of your existing muscle group detection logic

        if (exerciseNameLowerCase.includes('push') || exerciseNameLowerCase.includes('chest') ||
            exerciseNameLowerCase.includes('bench') || exerciseNameLowerCase.includes('fly')) {
            return 'Chest';
        }

        if (exerciseNameLowerCase.includes('pull') || exerciseNameLowerCase.includes('row') ||
            exerciseNameLowerCase.includes('chin') || exerciseNameLowerCase.includes('lat') ||
            exerciseNameLowerCase.includes('deadlift')) {
            return 'Back';
        }

        if (exerciseNameLowerCase.includes('squat') || exerciseNameLowerCase.includes('lunge') ||
            exerciseNameLowerCase.includes('leg') || exerciseNameLowerCase.includes('calf')) {
            return 'Legs';
        }

        if (exerciseNameLowerCase.includes('curl') || exerciseNameLowerCase.includes('tricep') ||
            exerciseNameLowerCase.includes('bicep') || exerciseNameLowerCase.includes('dip')) {
            return 'Arms';
        }

        if (exerciseNameLowerCase.includes('shoulder') || exerciseNameLowerCase.includes('raise') ||
            exerciseNameLowerCase.includes('shrug')) {
            return 'Shoulders';
        }

        if (exerciseNameLowerCase.includes('crunch') || exerciseNameLowerCase.includes('plank') ||
            exerciseNameLowerCase.includes('twist') || exerciseNameLowerCase.includes('ab') ||
            exerciseNameLowerCase.includes('core')) {
            return 'Core';
        }

        return 'Other';
    };

    const createCharts = () => {
        const colors = ['#4CAF50', '#2196F3', '#FF5722', '#00BCD4', '#FFEB3B', '#F44336', '#9E9E9E', '#9C27B0'];

        // Prepare data
        const muscleGroups = Object.keys(muscleGroupProgress);
        const repsData = Object.values(muscleGroupProgress);

        // Bar Chart
        if (barChartRef.current && window.Chart) {
            const ctx = barChartRef.current.getContext('2d');
            new window.Chart(ctx, {
                type: 'bar',
                data: {
                    labels: muscleGroups,
                    datasets: [{
                        label: 'Total Reps',
                        data: repsData,
                        backgroundColor: colors.slice(0, muscleGroups.length),
                        borderColor: colors.slice(0, muscleGroups.length),
                        borderWidth: 1,
                        borderRadius: 4,
                    }]
                },
                options: {
                    responsive: true,
                    plugins: {
                        title: {
                            display: true,
                            text: 'Muscle Group Progress'
                        },
                        legend: {
                            display: false
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'Total Reps'
                            }
                        },
                        x: {
                            title: {
                                display: true,
                                text: 'Muscle Groups'
                            }
                        }
                    }
                }
            });
        }

        // Pie Chart
        if (pieChartRef.current && window.Chart) {
            const ctx = pieChartRef.current.getContext('2d');
            new window.Chart(ctx, {
                type: 'pie',
                data: {
                    labels: muscleGroups,
                    datasets: [{
                        data: repsData,
                        backgroundColor: colors.slice(0, muscleGroups.length),
                        borderColor: '#fff',
                        borderWidth: 2
                    }]
                },
                options: {
                    responsive: true,
                    plugins: {
                        title: {
                            display: true,
                            text: 'Muscle Group Distribution'
                        },
                        legend: {
                            position: 'bottom'
                        }
                    }
                }
            });
        }

        // Line Chart - Workout Frequency Over Time
        const workoutFrequencyData = getWorkoutFrequencyData();
        if (lineChartRef.current && window.Chart && workoutFrequencyData.length > 1) {
            const ctx = lineChartRef.current.getContext('2d');
            new window.Chart(ctx, {
                type: 'line',
                data: {
                    labels: workoutFrequencyData.map(item => item.month),
                    datasets: [{
                        label: 'Workouts',
                        data: workoutFrequencyData.map(item => item.workouts),
                        borderColor: '#2196F3',
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#2196F3',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        pointRadius: 6
                    }]
                },
                options: {
                    responsive: true,
                    plugins: {
                        title: {
                            display: true,
                            text: 'Workout Frequency Over Time'
                        },
                        legend: {
                            display: false
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'Number of Workouts'
                            }
                        },
                        x: {
                            title: {
                                display: true,
                                text: 'Month'
                            }
                        }
                    }
                }
            });
        }
    };

    // Prepare workout frequency data over time
    const getWorkoutFrequencyData = () => {
        const frequencyMap = {};

        workoutHistory.forEach(workout => {
            try {
                const date = new Date(workout.date);
                if (!isNaN(date.getTime())) {
                    const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                    frequencyMap[monthYear] = (frequencyMap[monthYear] || 0) + 1;
                }
            } catch (e) {
                // Skip invalid dates
            }
        });

        return Object.entries(frequencyMap)
            .map(([month, count]) => ({
                month,
                workouts: count
            }))
            .sort((a, b) => new Date(a.month + ' 1') - new Date(b.month + ' 1'));
    };

    const workoutFrequencyData = getWorkoutFrequencyData();

    const handleEmailProgress = () => {
        const progressText = Object.entries(muscleGroupProgress)
            .map(([muscleGroup, reps]) => `${muscleGroup}: ${reps} reps`)
            .join('\n');

        const emailBody = `Muscle Group Progress Report:\n\n${progressText}\n\nTotal Workouts: ${workoutHistory.length}`;

        // Try to use Web Share API first
        if (navigator.share) {
            navigator.share({
                title: 'My Workout Progress',
                text: emailBody,
            }).catch(err => {
                console.error('Error sharing:', err);
            });
        } else {
            // Fallback to copying to clipboard
            navigator.clipboard.writeText(emailBody).then(() => {
                alert('Progress report copied to clipboard!');
            }).catch(err => {
                console.error('Error copying to clipboard:', err);
            });
        }
    };

    return (
        <div className="progress-graphs-container">
            <header className="progress-header">
                <button
                    className="back-button"
                    onClick={() => navigate('/')}
                >
                    ← Back to Home
                </button>
                <h1>Progress Graphs</h1>
                <p>{workoutHistory.length} workout{workoutHistory.length !== 1 ? 's' : ''} analyzed</p>
            </header>

            {Object.keys(muscleGroupProgress).length === 0 ? (
                <div className="no-data">
                    <p>No progress data available yet!</p>
                    <p>Complete and save some workouts to see your progress graphs.</p>
                    <button
                        className="start-workout-button"
                        onClick={() => navigate('/')}
                    >
                        Start Your First Workout
                    </button>
                </div>
            ) : (
                <div className="charts-container">
                    {/* Bar Chart */}
                    <div className="chart-section">
                        <h2>Muscle Group Progress (Bar Chart)</h2>
                        <div className="chart-wrapper">
                            <canvas ref={barChartRef} width="400" height="200"></canvas>
                        </div>
                    </div>

                    {/* Pie Chart */}
                    <div className="chart-section">
                        <h2>Muscle Group Distribution (Pie Chart)</h2>
                        <div className="chart-wrapper">
                            <canvas ref={pieChartRef} width="400" height="200"></canvas>
                        </div>
                    </div>

                    {/* Line Chart - Workout Frequency Over Time */}
                    {workoutFrequencyData.length > 1 && (
                        <div className="chart-section">
                            <h2>Workout Frequency Over Time</h2>
                            <div className="chart-wrapper">
                                <canvas ref={lineChartRef} width="400" height="200"></canvas>
                            </div>
                        </div>
                    )}

                    {/* Progress Summary */}
                    <div className="progress-summary">
                        <h2>Progress Summary</h2>
                        <div className="summary-stats">
                            <div className="summary-item">
                                <span className="stat-value">{workoutHistory.length}</span>
                                <span className="stat-label">Total Workouts</span>
                            </div>
                            <div className="summary-item">
                                <span className="stat-value">{Object.values(muscleGroupProgress).reduce((a, b) => a + b, 0)}</span>
                                <span className="stat-label">Total Reps</span>
                            </div>
                            <div className="summary-item">
                                <span className="stat-value">{Object.keys(muscleGroupProgress).length}</span>
                                <span className="stat-label">Muscle Groups</span>
                            </div>
                        </div>
                    </div>

                    {/* Email Progress Button */}
                    <div className="email-section">
                        <button
                            className="email-progress-button"
                            onClick={handleEmailProgress}
                        >
                            📧 Share Progress Report
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgressGraphs;